import jsPDF from "jspdf";
import { twMerge } from "tailwind-merge";
import { type ClassValue, clsx } from "clsx";
import { toast } from "react-toastify";
import slugifyLib from "slugify";

import {
  Circle,
  Slash,
  Square,
  Triangle,
  Type,
  Image,
  Component,
  Spline,
  Group,
} from "lucide-react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// will add new property to the fabric object, which is layerName.
// in consumer frontend, we gonna display more user-friendly names.
// we also gonna keep track of those if we fill a state, then go back to change design,
// then come back to designer again, we might be able to restore the input values.

export const getLayerNameByType = (type: string) => {
  switch (type) {
    // is not really a layer, but we gonna show it on designer frontend when we have activeSelection,
    // inside the element attributes.
    // is not gonna be on any real object in the canvas.
    case "activeSelection":
      return { en: "Active Selection", nl: "Actieve Selectie" };

    case "svg":
      return { en: "SVG", nl: "SVG", de: "SVG" };

    case "rect":
      return { en: "Rectangle", nl: "Rechthoek", de: "Rechteck" };

    case "circle":
      return { en: "Circle", nl: "Cirkel", de: "Kreis" };

    case "triangle":
      return { en: "Triangle", nl: "Driehoek", de: "Dreieck" };

    case "line":
      return { en: "Line", nl: "Lijn", de: "Linie" };

    case "i-text":
      return { en: "Text", nl: "Tekst", de: "Text" };

    case "curved-text":
      return { en: "Curved Text", nl: "Gebogen Tekst", de: "Gebogene Text" };

    case "group":
      return { en: "Group", nl: "Groep", de: "Gruppe" };

    case "image":
      return { en: "Image", nl: "Afbeelding", de: "Bild" };

    case "path":
      return { en: "Path", nl: "Pad", de: "Pfad" };

    default:
      return { en: "Layer", nl: "Laag", de: "Ebene" };
  }
};

export const getShapeInfo = (shapeType: string) => {
  switch (shapeType) {
    case "rect":
      return {
        icon: Square,
        name: "Rectangle",
      };

    case "circle":
      return {
        icon: Circle,
        name: "Circle",
      };

    case "triangle":
      return {
        icon: Triangle,
        name: "Triangle",
      };

    case "line":
      return {
        icon: Slash,
        name: "Line",
      };

    case "i-text":
      return {
        icon: Type,
        name: "Text",
      };

    case "curved-text":
      return {
        icon: Spline,
        name: "Curved Text",
      };

    case "group":
      return {
        icon: Group,
        name: "Group",
      };

    case "image":
      return {
        icon: Image,
        name: "Image",
      };

    case "path":
      return {
        icon: Component,
        name: "Path",
      };

    default:
      return {
        icon: Image,
        name: shapeType,
      };
  }
};

export function extractLastFourDigitsFromUUID(uuid: string) {
  return uuid.slice(-4);
}

export const exportToPdf = () => {
  const canvas = document.querySelector("canvas");

  if (!canvas) return;

  // use jspdf
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [canvas.width, canvas.height],
  });

  // get the canvas data url
  const data = canvas.toDataURL();

  // add the image to the pdf
  doc.addImage(data, "PNG", 0, 0, canvas.width, canvas.height);

  // download the pdf
  doc.save("canvas.pdf");
};

export const shouldShowTextInputBasedOnType = (type: string | null) => {
  // show only on i-text.
  if (type === "i-text") return true;

  // if type is any shape, then show the dimensions.
  return false;
};

export const shouldShowDimensionsBasedOnType = (type: string | null) => {
  // type can be any shape, i-text + group, activeSelection, or null.

  // only activeSelection should not show the dimensions. Group is handled by now.
  if (!type || type === "activeSelection") return false;

  // if type is any shape, then show the dimensions.
  return true;
};

export const shouldShowAlignmentControlBasedOnType = (type: string | null) => {
  // hide on group only.

  if (!type) return false;

  return true;
  // return type !== "group";
};

export const shouldShowAlignmentToSelectionControlBasedOnType = (
  type: string | null
) => {
  // hide for all except activeSelection.
  if (!type) return false;
  // only prevent on activeSelection.
  return type === "activeSelection";
};

export const shouldShowLayeringControlBasedOnType = (type: string | null) => {
  // hide on group only.

  if (!type) return false;

  // only prevent on activeSelection.
  return type !== "activeSelection";
};

export const shouldShowGroupControlBasedOnType = (type: string | null) => {
  // only show on group and activeSelection.

  if (!type) return false;

  return type === "group" || type === "activeSelection";
};

export const shouldShowTextBasedOnType = (type: string | null) => {
  // show only on i-text.

  if (!type) return false;

  return type === "i-text";
};

export const shouldShowCurvatureSettingsByAttributes = (elementAttrs: any) => {
  // show only on i-text.
  if (!elementAttrs) return false;

  if (!elementAttrs.type) return false;

  return elementAttrs.type === "i-text" && elementAttrs.isCurvedText;
};

export const shouldShowFillColorBasedOnType = (type: string | null) => {
  // show on text, and shapes (except line) and path (svg), not images.

  if (!type) return false;

  return (
    type !== "image" &&
    type !== "line" &&
    type !== "group" &&
    type !== "activeSelection"
  );
};

export const shouldShowStrokeSettingBasedOnType = (type: string | null) => {
  // show everywhere except images.

  if (!type) return false;

  return type !== "image" && type !== "group" && type !== "activeSelection";
};

export const shouldShowRotationSettingBasedOnType = (type: string | null) => {
  // show except activeSelection.

  if (!type) return false;

  return type !== "activeSelection";
};

export const slugify = (text: string) => {
  return slugifyLib(text, {
    lower: true,
    trim: true,
  });
};

export const notify = ({
  type,
  message,
}: {
  type: "success" | "error" | "info" | "warning" | undefined;
  message: string;
}) => {
  if (!type) {
    toast(message);
    return;
  }

  toast[type](message);
  return;
};
